// Web Audio API sound manager - synthesizes all SFX (no background music)
// No external files; works offline, zero licensing risk.

const PREF_KEY = "bizkids_sound_prefs_v3";

let audioCtx = null;
let masterGain = null;
let sfxGain = null;

const defaultPrefs = { sfxOn: true };

const loadPrefs = () => {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (raw) return { ...defaultPrefs, ...JSON.parse(raw) };
  } catch (e) {
    console.warn("Sound prefs could not be read", e);
  }
  return { ...defaultPrefs };
};

const savePrefs = (p) => {
  try {
    localStorage.setItem(PREF_KEY, JSON.stringify(p));
  } catch (e) {
    console.warn("Sound prefs could not be saved", e);
  }
};

let prefs = loadPrefs();

const ensureCtx = () => {
  if (audioCtx) return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  audioCtx = new AC();
  masterGain = audioCtx.createGain();
  masterGain.gain.value = 0.6;
  masterGain.connect(audioCtx.destination);

  sfxGain = audioCtx.createGain();
  sfxGain.gain.value = 1;
  sfxGain.connect(masterGain);
};

const resumeCtx = () => {
  if (!audioCtx) ensureCtx();
  if (audioCtx && audioCtx.state === "suspended") audioCtx.resume();
};

// Play a single note through the SFX chain
const note = (freq, dur, type = "sine", vol = 0.3, whenOffset = 0) => {
  if (!audioCtx) return;
  const t0 = audioCtx.currentTime + whenOffset;
  const osc = audioCtx.createOscillator();
  const g = audioCtx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(vol, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g);
  g.connect(sfxGain);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
};

// Pitch-swept SFX
const sweep = (fromF, toF, dur, type = "square", vol = 0.2) => {
  if (!audioCtx) return;
  const t0 = audioCtx.currentTime;
  const osc = audioCtx.createOscillator();
  const g = audioCtx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(fromF, t0);
  osc.frequency.exponentialRampToValueAtTime(Math.max(1, toF), t0 + dur);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(vol, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g);
  g.connect(sfxGain);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
};

// Pre-defined sound effects (bouncy, playful)
const SFX = {
  click: () => {
    if (!prefs.sfxOn) return;
    resumeCtx();
    note(660, 0.06, "square", 0.15);
  },
  tap: () => {
    if (!prefs.sfxOn) return;
    resumeCtx();
    note(880, 0.05, "triangle", 0.18);
  },
  coin: () => {
    if (!prefs.sfxOn) return;
    resumeCtx();
    // classic coin: two-tone rising
    note(988, 0.08, "square", 0.2, 0);
    note(1318, 0.14, "square", 0.2, 0.06);
  },
  success: () => {
    if (!prefs.sfxOn) return;
    resumeCtx();
    // arpeggio C-E-G-C
    [523, 659, 784, 1046].forEach((f, i) => note(f, 0.15, "triangle", 0.22, i * 0.08));
  },
  fanfare: () => {
    if (!prefs.sfxOn) return;
    resumeCtx();
    [523, 659, 784, 1046, 1318].forEach((f, i) =>
      note(f, 0.18, "square", 0.18, i * 0.09)
    );
  },
  error: () => {
    if (!prefs.sfxOn) return;
    resumeCtx();
    sweep(300, 120, 0.28, "sawtooth", 0.18);
  },
  purchase: () => {
    if (!prefs.sfxOn) return;
    resumeCtx();
    note(440, 0.06, "triangle", 0.18, 0);
    note(660, 0.06, "triangle", 0.18, 0.06);
    note(880, 0.12, "triangle", 0.22, 0.12);
  },
  whoosh: () => {
    if (!prefs.sfxOn) return;
    resumeCtx();
    sweep(200, 1200, 0.25, "sawtooth", 0.08);
  },
  pop: () => {
    if (!prefs.sfxOn) return;
    resumeCtx();
    note(500, 0.05, "sine", 0.22);
  },
};

export const Sound = {
  ...SFX,
  getPrefs: () => ({ ...prefs }),
  setSfx: (v) => {
    prefs.sfxOn = !!v;
    savePrefs(prefs);
  },
  toggleSfx: () => {
    prefs.sfxOn = !prefs.sfxOn;
    savePrefs(prefs);
    return prefs.sfxOn;
  },
  primeOnFirstInteraction: () => {
    // Called by a user click to unlock audio in browsers
    ensureCtx();
    resumeCtx();
  },
};

// Browsers block audio until the first tap/click
if (typeof window !== "undefined") {
  const unlock = () => {
    Sound.primeOnFirstInteraction();
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("keydown", unlock);
}
